import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellOff, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { When } from "./When";

const err = (e, d) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || d);

/** Who gets the "job failed twice in a row" e-mails, plus the jobs whose alerts are muted. */
export function AlertSettingsDialog({ canEdit, labels, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/cron/alerts").then(({ data }) => setCfg(data)).catch(() => setCfg(false)); }, []);
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/cron/alerts/test"); api.get("/cron/alerts").then((r) => setCfg(r.data)).catch(() => {}); toast.success(data.status === "paused" ? "Test alert held in the outbox (sending is paused)" : "Test alert sent"); }
    catch (e) { err(e, "Could not send the test alert"); } finally { setBusy(""); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="alert-settings-dialog">
        <DialogHeader><DialogTitle>Job failure alerts</DialogTitle>
          <DialogDescription>When a job fails {cfg?.streak || 2} times in a row these people get one e-mail, then one more when it works again.</DialogDescription></DialogHeader>
        {cfg === null && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-300" />}
        {cfg === false && <p className="text-sm text-slate-500" data-testid="alert-settings-error">Could not load the alert settings.</p>}
        {cfg && <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recipients</div>
            <div className="flex flex-wrap gap-1.5" data-testid="alert-recipients-list">{cfg.recipients.map((e) => (
              <span key={e} className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-700">{e}</span>))}</div>
            {!cfg.recipients.length && <p className="text-[11px] font-medium text-amber-700" data-testid="alert-no-recipients">Nobody yet — nobody is told when a job fails.</p>}
            <Link to="/settings?tab=notifications" onClick={onClose} className="inline-block text-[11px] font-medium text-sky-700 hover:underline" data-testid="alert-recipients-link">
              Choose recipients in Settings → Notifications → Admin alert recipients →</Link>
          </div>
          <div className="space-y-1.5" data-testid="alert-muted-list">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Muted jobs ({cfg.muted.length})</div>
            {cfg.muted.length ? <ul className="space-y-1 text-xs text-slate-600">{cfg.muted.map((k) => (
              <li key={k} className="flex items-center gap-1.5"><BellOff className="h-3 w-3 text-slate-400" />{labels[k] || k}</li>))}</ul>
              : <p className="text-xs text-slate-500">None — every job alerts. Use the bell on a job to mute it.</p>}
          </div>
          {cfg.last_test && <div className="text-[11px] text-slate-500" data-testid="alert-last-test">Last test: {cfg.last_test.status} by {cfg.last_test.by}<When iso={cfg.last_test.at} /></div>}
        </div>}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" disabled={!cfg || !canEdit || !cfg.recipients.length || !!busy} onClick={test} data-testid="alert-send-test">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send a test alert</Button>
          <Button variant="ghost" onClick={onClose} data-testid="alert-settings-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
